/**
 * SecureScoreEmptyState: the Secure Score tab when Microsoft has returned no
 * score for the tenant. Carbon order (DESIGN.md "The No-Kicker Rule"): the
 * headline names the subject and the next step, the body gives the likely
 * causes, then the actions (sync now, or open Microsoft Defender) and the
 * tenant ID for a support ticket. Props and the sync mutation are unchanged.
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { Button, ButtonLink } from '@/components/carbon/Button'
import { IconExternal } from '@/components/carbon/icons'
import { Tile } from '@/components/carbon/Tile'

interface SecureScoreEmptyStateProps {
  tenantId: string
}

export function SecureScoreEmptyState({ tenantId }: SecureScoreEmptyStateProps) {
  const queryClient = useQueryClient()
  const [isSyncing, setIsSyncing] = useState(false)

  const syncMutation = useMutation({
    mutationFn: () => api.refreshSecureScore(tenantId),
    onMutate: () => setIsSyncing(true),
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(['secureScoreSummary', tenantId], data)
        toast.success('Secure Score synced.')
      } else {
        toast.error('Microsoft returned no Secure Score data. Check the tenant permissions.')
      }
    },
    onError: () => {
      toast.error('Secure Score did not sync. Try again in a few minutes.')
    },
    onSettled: () => setIsSyncing(false),
  })

  return (
    <Tile as="section" padding={24} aria-labelledby="secure-score-empty-title" className="mt-8">
      <h1 id="secure-score-empty-title" className="m-0 max-w-[40ch] text-heading font-normal text-text-primary">
        Sync Microsoft Secure Score to see this tenant&rsquo;s score.
      </h1>
      <p className="m-0 mt-2 max-w-[56ch] text-body text-text-secondary">
        Microsoft has not returned a Secure Score for this tenant. One of these is the usual cause:
      </p>
      <ul className="m-0 mt-4 grid max-w-[56ch] list-disc gap-2 pl-5 text-body text-text-secondary marker:text-text-helper">
        <li>Microsoft 365 Defender is not enabled for the tenant.</li>
        <li>
          The Guardian app registration is missing the{' '}
          <code className="break-all font-mono text-[13px] text-text-primary">SecurityEvents.Read.All</code> permission.
        </li>
        <li>Microsoft has not generated a Secure Score for the tenant yet.</li>
      </ul>
      <div className="mt-8 flex flex-wrap gap-4 max-c-md:[&>*]:w-full max-c-md:[&>*]:max-w-none">
        <Button kind="primary" onClick={() => syncMutation.mutate()} loading={isSyncing}>
          {isSyncing ? 'Syncing Secure Score' : 'Sync Secure Score'}
        </Button>
        <ButtonLink href="https://security.microsoft.com/securescore" kind="tertiary" icon={<IconExternal />}>
          Open Microsoft Defender
        </ButtonLink>
      </div>
      <p className="m-0 mt-5 break-all text-helper text-text-helper">
        Tenant ID <span className="font-mono tabular-nums">{tenantId}</span>
      </p>
    </Tile>
  )
}
